import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Text, Title, Badge } from '@tremor/react';
import { ArrowPathIcon, ChevronDownIcon, ChevronUpIcon, MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useSidebar } from '../../../Components/ContextProviders/SidebarContextProvider';
import { getSidebarPadding } from '../../../utils/sidebar';
import SectionHeader from '../../../Components/GeneralComponents/SectionHeader';
import {
    getParticipants,
    createParticipant,
    updateParticipant,
    getParticipantAppearances,
} from '../../../Requests/ParticipantRequests';

function formatDate(iso) {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function ParticipantForm({ participant, onSave, onCancel, saving }) {
    const { t } = useTranslation();
    const [name, setName] = useState(participant?.name ?? '');
    const [email, setEmail] = useState(participant?.email ?? '');
    const [description, setDescription] = useState(participant?.description ?? '');

    return (
        <div className="flex flex-wrap items-start gap-2">
            <input
                className="rounded-md border-gray-300 text-sm shadow-sm focus:border-iavisus-100 focus:ring-iavisus-100"
                placeholder={t('Participants.NamePlaceholder')}
                value={name}
                onChange={(e) => setName(e.target.value)}
            />
            <input
                className="rounded-md border-gray-300 text-sm shadow-sm focus:border-iavisus-100 focus:ring-iavisus-100"
                placeholder={t('Participants.EmailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
            />
            <input
                className="flex-1 min-w-[12rem] rounded-md border-gray-300 text-sm shadow-sm focus:border-iavisus-100 focus:ring-iavisus-100"
                placeholder={t('Participants.NotesPlaceholder')}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
            />
            <button
                type="button"
                disabled={saving || !name.trim()}
                onClick={() => onSave({ name: name.trim(), email: email.trim() || null, description: description.trim() || null })}
                className="rounded-md bg-iavisus-100 px-3 py-2 text-xs font-medium text-white disabled:opacity-50"
            >
                {t('Participants.Save')}
            </button>
            <button type="button" onClick={onCancel} className="px-2 py-2 text-xs text-gray-500 hover:underline">
                {t('Participants.Cancel')}
            </button>
        </div>
    );
}

/**
 * Everywhere one participant shows up — questionnaires, calls and analyses —
 * loaded on demand when a row is opened.
 */
function Appearances({ participantUuid }) {
    const { t } = useTranslation();
    const [appearances, setAppearances] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        getParticipantAppearances(participantUuid)
            .then(res => setAppearances(res.data.appearances ?? []))
            .catch(() => setError(t('Participants.LoadAppearancesFailed')));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [participantUuid]);

    if (error) return <Text className="text-xs text-red-500">{error}</Text>;
    if (!appearances) return <Text className="text-xs text-gray-400">{t('Participants.Loading')}</Text>;
    if (appearances.length === 0) return <Text className="text-xs text-gray-400">{t('Participants.NoAppearances')}</Text>;

    const typeLabel = {
        questionnaire: t('Participants.SourceQuestionnaire'),
        conference: t('Participants.SourceConference'),
        analysis: t('Participants.SourceAnalysis'),
    };

    return (
        <ul className="divide-y divide-gray-100">
            {appearances.map((appearance, i) => (
                <li key={`${appearance.type}-${appearance.reference}-${i}`} className="flex items-center justify-between py-2">
                    <div className="flex items-center gap-3">
                        <Badge size="xs" color={appearance.type === 'questionnaire' ? 'blue' : appearance.type === 'conference' ? 'purple' : 'amber'}>
                            {typeLabel[appearance.type] ?? appearance.type}
                        </Badge>
                        <span className="text-sm text-gray-800">{appearance.title || '—'}</span>
                    </div>
                    <div className="flex items-center gap-3">
                        {appearance.link_method === 'email' && (
                            <Text className="text-xs text-gray-400">{t('Participants.MatchedByEmail')}</Text>
                        )}
                        <Text className="text-xs text-gray-500">{formatDate(appearance.happened_at)}</Text>
                    </div>
                </li>
            ))}
        </ul>
    );
}

function ParticipantsPage() {
    const { t } = useTranslation();
    const { isCollapsed } = useSidebar();

    const [participants, setParticipants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [saving, setSaving] = useState(false);
    const [creating, setCreating] = useState(false);
    const [editing, setEditing] = useState(null);
    const [open, setOpen] = useState(null);

    const load = (term = '') => {
        setLoading(true);
        getParticipants(term)
            .then(res => setParticipants(res.data.participants ?? []))
            .catch(() => setError(t('Participants.LoadFailed')))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const create = async (payload) => {
        setSaving(true);
        try {
            await createParticipant(payload);
            setCreating(false);
            load(search);
        } catch {
            setError(t('Participants.SaveFailed'));
        }
        setSaving(false);
    };

    const update = async (participantUuid, payload) => {
        setSaving(true);
        try {
            await updateParticipant(participantUuid, payload);
            setEditing(null);
            load(search);
        } catch {
            setError(t('Participants.SaveFailed'));
        }
        setSaving(false);
    };

    return (
        <div className={`${getSidebarPadding(isCollapsed)} transition-all duration-300 py-12 w-full flex justify-center`}>
            <div className="flex flex-col w-full justify-center items-center gap-4">
                <div className="bg-white max-w-7xl rounded-2xl w-full shadow-xl mx-8">
                    <SectionHeader
                        title={t('Participants.Title')}
                        subtitle={t('Participants.Subtitle')}
                        breadcrubs={[{ name: t('Participants.Title') }]}
                    />
                </div>

                <div className="bg-white max-w-7xl rounded-2xl w-full shadow-xl mx-8 mb-8 px-6 py-5">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                        <div className="relative">
                            <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                            <input
                                className="pl-9 rounded-md border-gray-300 text-sm shadow-sm focus:border-iavisus-100 focus:ring-iavisus-100"
                                placeholder={t('Participants.SearchPlaceholder')}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && load(search)}
                            />
                        </div>
                        {!creating && (
                            <button
                                type="button"
                                onClick={() => setCreating(true)}
                                className="inline-flex items-center gap-1.5 rounded-md bg-iavisus-100 px-3 py-2 text-xs font-medium text-white"
                            >
                                <PlusIcon className="h-4 w-4" /> {t('Participants.New')}
                            </button>
                        )}
                    </div>

                    {creating && (
                        <Card className="mb-4 py-3">
                            <ParticipantForm onSave={create} onCancel={() => setCreating(false)} saving={saving} />
                        </Card>
                    )}

                    {error && <Text className="text-red-500 mb-3">{error}</Text>}

                    {loading ? (
                        <div className="flex items-center justify-center py-16">
                            <ArrowPathIcon className="h-6 w-6 animate-spin text-gray-400 mr-2" />
                            <Text>{t('Participants.Loading')}</Text>
                        </div>
                    ) : participants.length === 0 ? (
                        <Text className="text-gray-500 text-center py-12">{t('Participants.Empty')}</Text>
                    ) : (
                        <div className="space-y-2">
                            {participants.map(participant => {
                                const isOpen = open === participant.uuid;
                                const total = (participant.questionnaire_count ?? 0)
                                    + (participant.conference_count ?? 0)
                                    + (participant.analysis_count ?? 0);

                                return (
                                    <Card key={participant.uuid} className="py-3">
                                        {editing === participant.uuid ? (
                                            <ParticipantForm
                                                participant={participant}
                                                saving={saving}
                                                onCancel={() => setEditing(null)}
                                                onSave={(payload) => update(participant.uuid, payload)}
                                            />
                                        ) : (
                                            <div className="flex flex-wrap items-center justify-between gap-3">
                                                <div className="flex items-center gap-3 min-w-0">
                                                    <Badge color="indigo" size="xs">{participant.code}</Badge>
                                                    <span className="text-sm font-medium text-gray-900 truncate">{participant.name}</span>
                                                    {participant.email && (
                                                        <Text className="text-xs text-gray-500 truncate">{participant.email}</Text>
                                                    )}
                                                </div>

                                                <div className="flex items-center gap-4">
                                                    <Text className="text-xs text-gray-500">
                                                        {t('Participants.SourceCounts', {
                                                            questionnaires: participant.questionnaire_count ?? 0,
                                                            conferences: participant.conference_count ?? 0,
                                                            analyses: participant.analysis_count ?? 0,
                                                        })}
                                                    </Text>
                                                    <button
                                                        type="button"
                                                        onClick={() => setEditing(participant.uuid)}
                                                        className="text-xs text-iavisus-100 hover:underline"
                                                    >
                                                        {t('Participants.Edit')}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setOpen(isOpen ? null : participant.uuid)}
                                                        disabled={total === 0}
                                                        className="disabled:opacity-30"
                                                        aria-label={t('Participants.ToggleAppearances')}
                                                    >
                                                        {isOpen
                                                            ? <ChevronUpIcon className="h-4 w-4 text-gray-400" />
                                                            : <ChevronDownIcon className="h-4 w-4 text-gray-400" />}
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {isOpen && (
                                            <div className="mt-3 border-t border-gray-100 pt-3">
                                                <Appearances participantUuid={participant.uuid} />
                                            </div>
                                        )}
                                    </Card>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ParticipantsPage;
